import { IndexPagination } from '@/components/shared/index-pagination';
import { isIndexCollectionBlock } from '@/lib/blocks/collection/index-collection/blocks';
import {
  findIndexListCollectionSubtree,
  findIndexPinnedCollectionSubtree,
  resolveIndexFilterBlock,
} from '@/lib/blocks/collection/index-collection/registry';
import { readString } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';
import type { PaginationInfo } from '@/types/pagination';
import {
  type IndexFilterOption,
  type IndexFilterOrdering,
  readIndexLayoutProps,
  sortIndexFilterOptions,
} from './index-section-utils';
import { usePageSectionContext } from './page-section-context';

export interface IndexSectionRecordIdentity {
  id?: string;
  path?: string;
}

export function resolveIndexCollectionHeading(heading?: string | null) {
  return heading?.trim() || undefined;
}

export function useIndexSection<RecordType extends IndexSectionRecordIdentity>({
  records,
  pinnedRecords,
  filters,
  filterOrdering,
  backgroundProps,
  pagination,
}: {
  records: RecordType[];
  pinnedRecords?: RecordType[];
  filters: IndexFilterOption[];
  filterOrdering?: IndexFilterOrdering;
  backgroundProps: Record<string, unknown>;
  pagination?: PaginationInfo;
}) {
  const sectionContext = usePageSectionContext();
  const sortedFilters = sortIndexFilterOptions({
    items: filters,
    ordering: filterOrdering,
  });
  const hasRecords = records.length > 0;
  const resolvedPinnedRecords = pinnedRecords ?? [];
  const visibleRecords = records;
  const shouldShowPinned = resolvedPinnedRecords.length > 0;
  const hasVisibleRecords = visibleRecords.length > 0;
  const sectionBackgroundColor = readString(backgroundProps.backgroundColor);
  const layoutProps = readIndexLayoutProps(backgroundProps);
  const indexBlocks = sectionContext?.blocks ?? [];
  const pinnedCollectionBlocks = findIndexPinnedCollectionSubtree(indexBlocks);
  const listCollectionBlocks = findIndexListCollectionSubtree(indexBlocks);
  const filterBlock = resolveIndexFilterBlock(indexBlocks);
  const hasPinnedCollectionBlocks = pinnedCollectionBlocks.some((block: PageBlock) =>
    isIndexCollectionBlock(block),
  );
  const hasListCollectionBlocks = listCollectionBlocks.some((block: PageBlock) =>
    isIndexCollectionBlock(block),
  );
  const paginationNode =
    hasRecords && pagination ? <IndexPagination pagination={pagination} fullWidth /> : null;

  return {
    sortedFilters,
    hasRecords,
    shouldShowPinned,
    pinnedRecords: resolvedPinnedRecords,
    visibleRecords,
    hasVisibleRecords,
    sectionBackgroundColor,
    layoutProps,
    pinnedCollectionBlocks,
    listCollectionBlocks,
    filterBlock,
    hasPinnedCollectionBlocks,
    hasListCollectionBlocks,
    paginationNode,
  };
}
